import React, { useEffect } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { SIGNUP_URL } from '../engagement/engagementContent';

const SECTIONS = [
  {
    num: '01',
    title: 'Every customer can be known.',
    body: (
      <>
        <p>Customer relationships used to be constrained by headcount.</p>
        <p>
          A human could understand a few hundred accounts. Software could send messages to millions, but knew almost nothing about the person receiving them.
        </p>
        <p>AI removes that tradeoff.</p>
        <p>
          Every customer can now have an agent that understands who they are, what they have done, what they are trying to accomplish, and what they may need next.
        </p>
      </>
    ),
  },
  {
    num: '02',
    title: 'Product usage is a language.',
    body: (
      <>
        <p>A customer does not need to tell you they are struggling.</p>
        <p>They already tell you through what they do.</p>
        <p>
          What they configured. What they ignored. Which feature they tried. Where they stopped. What they use every morning. What they have not touched in three weeks.
        </p>
        <p>
          Agents can continuously read these signals and turn product behavior into understanding.
        </p>
      </>
    ),
  },
  {
    num: '03',
    title: 'Your product can explain itself.',
    body: (
      <>
        <p>
          Support agents used to know tickets. Analytics tools knew events. CRMs knew accounts. Documentation knew the product.
        </p>
        <p>An AI agent can know all of them.</p>
        <p>
          Product documentation, account history, conversations, billing, integrations, feature usage and behavioral patterns can become one living context.
        </p>
        <p>The result is not another chatbot.</p>
        <p>
          It is an agent that understands both{' '}
          <strong className="font-semibold text-gray-900">your product and the person using it.</strong>
        </p>
      </>
    ),
  },
  {
    num: '04',
    title: 'The next message should belong to one person.',
    body: (
      <>
        <p>
          Segments were necessary because software could not reason about every customer individually.
        </p>
        <div className="space-y-1 text-gray-500">
          <p>“Users inactive for 14 days.”</p>
          <p>“Trial users.”</p>
          <p>“Customers on Pro.”</p>
        </div>
        <p>Those are useful abstractions, but customers are not segments.</p>
        <p>
          AI makes it possible to decide what to say, when to say it, and whether to say anything at all based on the context of one customer.
        </p>
        <p>Millions of customers. Millions of different conversations.</p>
      </>
    ),
  },
  {
    num: '05',
    title: 'Customer success becomes continuous.',
    body: (
      <>
        <p>
          Traditional customer success waits for meetings, tickets, health-score alerts and quarterly reviews.
        </p>
        <p>Agents do not have to wait.</p>
        <p>
          They can notice the moment someone gets stuck, recognize an unused capability, explain a feature in context, celebrate progress, surface an opportunity or reconnect with someone drifting away.
        </p>
        <p>The relationship can exist between the meetings.</p>
      </>
    ),
  },
  {
    num: '06',
    title: 'Intimacy can finally scale.',
    body: (
      <>
        <p>The internet gave companies distribution at enormous scale.</p>
        <p>
          AI gives them something different:{' '}
          <strong className="font-semibold text-gray-900">attention at enormous scale.</strong>
        </p>
        <p>
          The companies of the next decade will not just have better funnels or more automated lifecycle emails.
        </p>
        <p>They will build products where every customer feels understood.</p>
        <p>That is what Kolanut is building toward.</p>
        <p className="font-serif text-xl sm:text-2xl text-gray-900 leading-snug pt-2">
          One intelligent relationship for every customer.
        </p>
      </>
    ),
  },
];

const KolanutThesisPage = () => {
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />

      <main className="flex-grow pb-20 sm:pb-0">
        <article className="bg-white pt-10 sm:pt-14 md:pt-20 pb-16 sm:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <header className="grid lg:grid-cols-12 gap-6 lg:gap-x-12 lg:items-start">
              <h1 className="lg:col-span-5 font-serif text-[2.35rem] leading-[1.15] text-gray-900 sm:text-[2.75rem] md:text-5xl">
                What we believe
              </h1>
              <div className="lg:col-span-6 lg:col-start-7 max-w-[40rem] space-y-5 text-[1.0625rem] sm:text-lg leading-[1.75] text-gray-700">
                <p>Software has always been able to serve millions of customers.</p>
                <p>
                  Until now, it could not{' '}
                  <strong className="font-semibold text-gray-900">know millions of customers.</strong>
                </p>
                <p>Kolanut is built on a simple belief: AI changes that.</p>
                <p>
                  For the first time, every customer can have an intelligent relationship with a company that understands the product, understands how they use it, remembers their history, and knows when to help.
                </p>
                <p>This is the future we are building for.</p>
              </div>
            </header>

            <div className="mt-16 sm:mt-24 space-y-14 sm:space-y-16 lg:space-y-20">
              {SECTIONS.map((section) => (
                <section
                  key={section.num}
                  className="grid lg:grid-cols-12 gap-3 lg:gap-x-12 lg:items-start"
                >
                  <div className="lg:col-span-5">
                    <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-gray-400">
                      {section.num}
                    </p>
                    <h2 className="mt-3 max-w-md font-serif text-2xl sm:text-[1.75rem] leading-[1.25] text-gray-900">
                      {section.title}
                    </h2>
                  </div>
                  <div className="lg:col-span-6 lg:col-start-7 max-w-[40rem] space-y-5 text-[1.0625rem] sm:text-lg leading-[1.75] text-gray-700">
                    {section.body}
                  </div>
                </section>
              ))}
            </div>

            <div className="mt-14 sm:mt-20">
              <a
                href={SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center h-12 px-8 rounded-full bg-[#8ae690] text-black font-semibold text-sm shadow-md hover:bg-[#7ad67f] hover:shadow-lg transition-all duration-200"
              >
                Get Started
              </a>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default KolanutThesisPage;
